// =============================================================================
// src/lib/user-profile.ts
// Hooks for reading + updating the user_profiles row + active organization.
//
// useUserProfile()    — reads user_profiles for the current auth.uid().
// useActiveOrg()      — reads org_summary RPC for user_profiles.active_organization_id.
// useUpdateProfile()  — updates first_name / last_name / phone.
// =============================================================================

import { useCallback, useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'

export interface UserProfile {
  user_id: string
  first_name: string | null
  last_name: string | null
  phone: string | null
  display_name: string | null
  active_organization_id: string | null
  rate_limit_tier: string
  is_super_admin: boolean
  is_active: boolean
}

export interface ActiveOrgSummary {
  organization_id: string
  name: string
  slug: string
  created_at: string
  caller_role: 'owner' | 'admin' | 'member' | null
  member_count: number
  pending_invite_count: number
}

interface UseUserProfileResult {
  profile: UserProfile | null
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

export function useUserProfile(): UseUserProfileResult {
  const { user } = useAuth()
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!user) {
      setProfile(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    const { data, error: err } = await supabase
      .from('user_profiles')
      .select(
        'user_id, first_name, last_name, phone, display_name, active_organization_id, rate_limit_tier, is_super_admin, is_active',
      )
      .eq('user_id', user.id)
      .maybeSingle()
    if (err) {
      setError(err.message)
      setProfile(null)
    } else {
      setProfile(data as UserProfile | null)
    }
    setLoading(false)
  }, [user])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load()
  }, [load])

  return { profile, loading, error, refresh: load }
}

interface UseActiveOrgResult {
  org: ActiveOrgSummary | null
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

export function useActiveOrg(): UseActiveOrgResult {
  // useUserProfile here is a *separate* React state instance from any
  // useUserProfile call in a component using us — they don't share
  // state. That matters for refresh: when a caller flips the active
  // org via switchActiveOrg, they need both their own profile AND
  // ours to re-read active_organization_id. Without that, our
  // [activeOrgId] effect dep never changes and `org` stays stale.
  // refresh() below chains through to refreshProfile so the activeOrgId
  // value driving this hook actually updates.
  const { profile, refresh: refreshProfile } = useUserProfile()
  const activeOrgId = profile?.active_organization_id ?? null
  const [org, setOrg] = useState<ActiveOrgSummary | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!activeOrgId) {
      setOrg(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    const { data, error: err } = await supabase.rpc('org_summary', {
      p_org_id: activeOrgId,
    })
    if (err) {
      setError(err.message)
      setOrg(null)
    } else {
      const row = Array.isArray(data) ? data[0] : null
      setOrg(row as ActiveOrgSummary | null)
    }
    setLoading(false)
  }, [activeOrgId])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load()
  }, [load])

  const refresh = useCallback(async () => {
    // Two reasons callers invoke refresh:
    //   (a) the user switched active org — handled by chaining through
    //       to refreshProfile so the internal profile sees the new
    //       active_organization_id, which then changes activeOrgId and
    //       re-fires the [activeOrgId] effect.
    //   (b) the org changed in place (e.g. renamed via updateOrgName)
    //       and activeOrgId is the same UUID — the [activeOrgId] dep
    //       doesn't change, so the effect won't re-fire on its own.
    // Call both in sequence: refreshProfile keeps consumers up to date,
    // then load() guarantees org_summary is re-fetched even when the
    // id didn't move. (PR #12 review.)
    await refreshProfile()
    await load()
  }, [refreshProfile, load])

  return { org, loading, error, refresh }
}

export interface ProfileUpdates {
  first_name?: string | null
  last_name?: string | null
  phone?: string | null
}

/**
 * Update the signed-in user's profile row. Returns the updated row on
 * success, or { error } on failure. The display_name (used in some UI
 * surfaces) is computed from first/last on save so it stays in sync.
 */
export async function updateUserProfile(
  userId: string,
  updates: ProfileUpdates,
): Promise<{ data: UserProfile | null; error: string | null }> {
  const display_name =
    [updates.first_name?.trim(), updates.last_name?.trim()]
      .filter((s) => s && s.length > 0)
      .join(' ') || null
  const { data, error } = await supabase
    .from('user_profiles')
    .update({ ...updates, display_name })
    .eq('user_id', userId)
    .select(
      'user_id, first_name, last_name, phone, display_name, active_organization_id, rate_limit_tier, is_super_admin, is_active',
    )
    .maybeSingle()
  if (error) return { data: null, error: error.message }
  return { data: data as UserProfile | null, error: null }
}
